import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import TestHeader from "../components/TestHeader/TestHeader";
import QuestionCard from "../components/QuestionCard/QuestionCard";
import ModalBox from "../components/ModalBox";
import "./ApplicationTestPage.styled.css";

function ApplicationTestPage() {
    const [answers, setAnswers] = useState({});
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [testData, setTestData] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);

    const [checkCodeInput, setCheckCodeInput] = useState("");
    const [checkInfo, setCheckInfo] = useState(null);
    const [checkError, setCheckError] = useState("");

    const answersRef = useRef({});

    // mode: "test" | "preview" | "check"
    const { mode, applicationCode } = useParams();
    const navigate = useNavigate();

    const location = useLocation();

    const effectiveMode = (() => {
        if (mode) return mode;
        if (location.pathname.startsWith("/test/")) return "test";
        if (location.pathname.startsWith("/preview/")) return "preview";
        return "check";
    })();
    const isPreview = effectiveMode === "preview";
    const isCheck = effectiveMode === "check";
    const isTest = effectiveMode === "test";

    const code = applicationCode || checkCodeInput;

    const handleSelectAnswer = (questionId, optionKey, multiple) => {
        setAnswers((prev) => {
            const current = prev[questionId] || [];

            if (!multiple) {
                return { ...prev, [questionId]: [optionKey] };
            }

            if (current.includes(optionKey)) {
                return {
                    ...prev,
                    [questionId]: current.filter((k) => k !== optionKey),
                };
            }

            return {
                ...prev,
                [questionId]: [...current, optionKey],
            };
        });
    };

    const runCheck = async (appCode) => {
        setCheckError("");
        setLoading(true);
        try {
            const res = await api.post("/test/check-code", {
                application_code: appCode,
            });

            setCheckInfo(res.data);
            setCheckCodeInput(appCode);
        } catch (err) {
            const msg =
                err?.response?.data?.error ||
                "Invalid or expired application code";
            setCheckInfo(null);
            setCheckError(msg);
        } finally {
            setLoading(false);
        }
    };

    const submitTest = useCallback(
        async (questionsOverride) => {
            const qs = questionsOverride || testData?.questions || [];

            await api.post("/test/submit", {
                application_code: code,
                answers: (qs || []).map((q) => ({
                    question_id: q.id,
                    selected: answers[q.id] || [],
                })),
            });

            window.location.href = "/thank-you";
        },
        [answers, code, testData]
    );

    useEffect(() => {
        if (!isTest || remainingSeconds <= 0) return;

        const timer = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    submitTest(); // AUTO SUBMIT
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isTest, remainingSeconds, submitTest]);


    // Report unfocus event
    const lastUnfocusRef = useRef(0);

    const reportUnfocus = useCallback((type) => {
        const now = Date.now();

        // Ignore if events fire too close (e.g. blur + pagehide)
        if (now - lastUnfocusRef.current < 2000) {
            return;
        }

        lastUnfocusRef.current = now;

        if (!code || !isTest) return;

        api.post("/candidates/unfocus", {
            application_code: code,
            event_type: type,
        }).catch(() => {});
    }, [code, isTest]);

    useEffect(() => {
        const onBlur = () => reportUnfocus("blur");
        window.addEventListener("blur", onBlur);
        return () => window.removeEventListener("blur", onBlur);
    }, [reportUnfocus]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                reportUnfocus("visibility_hidden");
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [reportUnfocus]);

    useEffect(() => {
        const onPageHide = () => reportUnfocus("pagehide");
        window.addEventListener("pagehide", onPageHide);
        return () => window.removeEventListener("pagehide", onPageHide);
    }, [reportUnfocus]);

    // End Report unfocus event

    useEffect(() => {
        if (applicationCode) {
            setCheckCodeInput(applicationCode);
        }
    }, [applicationCode]);

    useEffect(() => {
        answersRef.current = answers;
    }, [answers]);

    useEffect(() => {
        async function submitExpiredTest(questions) {
            await api.post("/test/submit", {
                application_code: code,
                answers: (questions || []).map((q) => ({
                    question_id: q.id,
                    selected: answersRef.current[q.id] || [],
                })),
            });

            window.location.href = "/thank-you";
        }

        async function init() {
            try {
                if (isCheck) {
                    // Auto-check if code is provided in URL
                    if (applicationCode) {
                        await runCheck(applicationCode);
                    } else {
                        setLoading(false);
                    }
                    return;
                }

                // preview / test must have code
                if (!code) {
                    setTestData(null);
                    setLoading(false);
                    return;
                }

                const res = await api.post("/test/check-code", {
                    application_code: code,
                });

                setTestData({
                    jobTitle: res.data.job.title,
                    candidate: res.data.candidate,
                });

                if (isPreview) {
                    const res = await api.post("/test/preview", {
                        application_code: code,
                    });

                    setTestData((prev) => ({
                        ...prev,
                        questions: res.data.questions,
                    }));
                } else if (isTest) {
                    const startRes = await api.post("/test/start", {
                        application_code: code,
                    });

                    if (startRes.data.remaining_seconds <= 0) {
                        await submitExpiredTest(startRes.data.questions);
                        return;
                    }

                    setRemainingSeconds(startRes.data.remaining_seconds);
                    setTestData((prev) => ({
                        ...prev,
                        questions: startRes.data.questions,
                    }));
                } else {
                    setTestData(null);
                }
            } catch (err) {
                alert("Invalid or expired application code");
            } finally {
                setLoading(false);
            }
        }

        init();
    }, [applicationCode, checkCodeInput, code, isPreview, isTest, isCheck]);

    if (isCheck) {
        return (
            <div className="application-test-page">

                <div className="check-wrap">
                    <div className="check-card">

                        <div className="login-backline">
                            <button
                                className="login-back"
                                onClick={() => navigate("/")}
                                type="button"
                            >
                                ← Back
                            </button>
                        </div>
                        <div className="check-title">Enter your application code</div>
                        <div className="check-subtitle">
                            We’ll verify your test status before you start.
                        </div>

                        <div className="check-row">
                            <input
                                className="check-input"
                                value={checkCodeInput}
                                onChange={(e) => setCheckCodeInput(e.target.value)}
                                placeholder="e.g. A1B2C3D4"
                            />
                            <button
                                className="check-button"
                                onClick={() => runCheck(checkCodeInput)}
                                disabled={!checkCodeInput || loading}
                                type="button"
                            >
                                {loading ? "Checking..." : "Check"}
                            </button>
                        </div>

                        {checkError && (
                            <div className="check-error">{checkError}</div>
                        )}

                        {checkInfo && (
                            <div className="check-info">
                                <div className="check-info-row">
                                    <div className="check-label">Job</div>
                                    <div className="check-value">{checkInfo.job?.title}</div>
                                </div>
                                <div className="check-info-row">
                                    <div className="check-label">Candidate</div>
                                    <div className="check-value">
                                        {checkInfo.candidate?.full_name} ({checkInfo.candidate?.email})
                                    </div>
                                </div>
                                <div className="check-info-row">
                                    <div className="check-label">Duration</div>
                                    <div className="check-value">{checkInfo.test_duration_minutes} minutes</div>
                                </div>

                                <div className="check-warning">
                                    <strong>Important / Lưu ý:</strong>
                                    <ul>
                                        <li>
                                            <b>VN:</b> Trong quá trình làm bài, hệ thống sẽ ghi nhận số lần bạn chuyển tab hoặc không tập trung vào bài test.
                                            Vui lòng hạn chế các hành động này.
                                        </li>
                                        <li>
                                            Nếu bạn làm mới (refresh) trang, các đáp án đã chọn có thể bị mất.
                                            Nếu đóng trình duyệt trong thời gian làm bài, bạn vẫn có thể quay lại bằng mã ứng tuyển khi thời gian chưa hết.
                                        </li>
                                        <li>
                                            <b>EN:</b> During the test, the system will monitor and record tab switching or loss of focus.
                                            Please stay focused on the test.
                                        </li>
                                        <li>
                                            Refreshing the page may cause your selected answers to be lost.
                                            If you close the browser, you can re-enter the test using your application code as long as time remains.
                                        </li>
                                    </ul>
                                </div>

                                <div className="check-actions">
                                    <button
                                        className="check-start"
                                        onClick={() => navigate(`/test/${code}`)}
                                        type="button"
                                    >
                                        Start
                                    </button>
                                </div>

                                <div className="check-note">
                                    By starting, your timer begins immediately.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="application-test-page">
                <div style={{ padding: "2rem", textAlign: "center" }}>
                    Loading test...
                </div>
            </div>
        );
    }

    if (!testData) {
        return (
            <div className="application-test-page">
                <div style={{ padding: "2rem", textAlign: "center" }}>
                    Test not found.
                </div>
            </div>
        );
    }

    return (
        <div className="application-test-page">
            <TestHeader
                jobTitle={testData.jobTitle}
                candidate={testData.candidate}
                duration={remainingSeconds}
                showTimer={!isPreview}
                showSubmit={!isPreview}
                onSubmit={() => setShowSubmitModal(true)}
            />

            <div className="questions-container">
                {(testData.questions || []).map((q, index) => (
                    <QuestionCard
                        key={q.id}
                        index={index + 1}
                        data={q}
                        selectedAnswers={answers[q.id] || []}
                        onSelect={handleSelectAnswer}
                        disabled={false}
                    />
                ))}
            </div>

            {showSubmitModal && (
                <ModalBox title="Submit test?">
                    <p>
                        <b>VN:</b> Hãy chắc chắn rằng bạn đã chọn đầy đủ các đáp án trước khi nộp bài.
                        Các câu chưa chọn có thể không được hệ thống ghi nhận.
                        <br />
                        <b>EN:</b> Please make sure you have selected all answers before submitting.
                        Unanswered questions may not be recorded by the system.
                    </p>
                    <p>
                        Are you sure you want to submit your answers?
                        You cannot change them later.
                    </p>
                    <div className="modal-buttons">
                        <button
                            className="danger"
                            onClick={async () => {
                                setShowSubmitModal(false);
                                await api.post("/test/submit", {
                                    application_code: code,
                                    answers: Object.entries(answers).map(([qid, selected]) => ({
                                        question_id: Number(qid),
                                        selected, // ["A","C"]
                                    })),
                                });

                                window.location.href = "/thank-you";
                            }}
                        >
                            Submit
                        </button>
                        <button onClick={() => setShowSubmitModal(false)}>
                            Wait...
                        </button>
                    </div>
                </ModalBox>
            )}
        </div>
    );
}

export default ApplicationTestPage;
